import {
  balances,
  chartSeries,
  fmtEur,
  methodMix,
  payIns,
  payOuts,
  splitAmount,
} from "@/shared/data";
import { buildBalanceChart, type BalanceChart } from "@/shared/chart";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

// ─── Sparkline (SVG, simple) ─────────────────────────────────
function spark(values: number[], opts: { color: string; height?: number }): string {
  const h = opts.height ?? 32;
  const w = 100;
  if (values.length < 2) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = w / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(2)},${(h - ((v - min) / range) * h).toFixed(2)}`)
    .join(" ");
  const last = values[values.length - 1] ?? 0;
  const lastY = h - ((last - min) / range) * h;
  return `<svg class="triptych-cell-spark" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none">
    <polyline points="${points}" fill="none" stroke="${opts.color}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <circle cx="${w}" cy="${lastY.toFixed(2)}" r="2" fill="${opts.color}" />
  </svg>`;
}

function balanceCell(
  b: typeof balances.payIn,
  spark: string,
  trendIsDown = false,
): string {
  const { whole, frac } = splitAmount(b.amount);
  const sign = b.deltaPct >= 0 ? "↑" : "↓";
  const downCls = trendIsDown || b.deltaPct < 0 ? " is-down" : "";
  return `<div class="triptych-cell">
    <div class="triptych-cell-label">
      ${b.label}
      <span class="triptych-cell-tag">${b.tag}</span>
    </div>
    <div class="triptych-cell-value">
      <span class="triptych-cell-value-cur">€</span>
      <span>${whole}</span>
      <span class="triptych-cell-value-frac">${frac}</span>
    </div>
    <div class="triptych-cell-trend">
      <strong class="${downCls.trim()}">${sign} ${Math.abs(b.deltaPct).toFixed(1)}%</strong>
      <span>${b.deltaLabel}</span>
    </div>
    ${spark}
    <div class="triptych-cell-foot">
      <span>${b.foot}</span>
      <span class="triptych-cell-foot-tag">§ ${b.note.split(" · ")[0]}</span>
    </div>
  </div>`;
}

function transactionRow(t: (typeof payIns)[number], showCols = "1"): string {
  const sign = t.direction === "in" ? "+" : "−";
  return `<div class="ledger-table-row" data-show="${showCols}" style="grid-template-columns: 90px 1.6fr 1fr 1fr 110px 30px;">
    <div class="col-date">
      <strong>${t.date}</strong>
      ${t.time}
    </div>
    <div class="col-name">
      <strong>${t.customer}</strong>
      <span>REF · ${t.id} · ${t.ref}</span>
    </div>
    <div class="col-method">
      <span class="col-method-icon is-${t.method === "sepa" ? "card" : t.method}">${t.method[0]?.toUpperCase()}</span>
      ${t.methodLabel}
    </div>
    <div class="col-amount is-${t.direction}">
      ${sign}${fmtEur(t.amountEur, { compact: false })}
      ${t.amountText ? `<span class="col-amount-sub">${t.amountText}</span>` : ""}
    </div>
    <div class="col-status">
      <span class="status-seal is-${t.status}"></span> ${t.statusLabel}
    </div>
    <div class="col-chevron">→</div>
  </div>`;
}

export function renderDashboard(ctx: Ctx): () => void {
  const inSpark = chartSeries.payIn.slice(-14);
  const outSpark = chartSeries.payOut.slice(-14);
  // Operational sparkline — a smooth synthetic curve so the cell has visual weight.
  const opSpark = inSpark.map((v, i) => Math.round(v * 0.75 + i * 1200));

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const recent = [...payIns.slice(0, 4), ...payOuts.slice(0, 2)]
    .sort(() => 0)
    .slice(0, 6);

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Daily Ledger<span class="page-eyebrow-rule"></span>${today}</span>
        <h1 class="page-title">Dashboard</h1>
      </div>
      <div class="page-head-meta">
        <span class="page-head-date">Reporting Window</span>
        <div class="date-range" id="page-range" role="group" aria-label="Date range">
          <button data-days="7">7d</button>
          <button data-days="30" class="is-active">30d</button>
          <button data-days="90">90d</button>
        </div>
        <span style="font-size: 0.7rem; color: var(--ink-subtle);">Last reconciled · 06 May, 09:14 CET</span>
      </div>
    </section>

    <section class="triptych" aria-label="Balances">
      ${balanceCell(balances.payIn, spark(inSpark, { color: "var(--accent)" }))}
      ${balanceCell(balances.payOut, spark(outSpark, { color: "var(--state-warning)" }), true)}
      ${balanceCell(balances.operational, spark(opSpark, { color: "var(--ink-primary)" }))}
    </section>

    <section class="grid-2-1">
      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Cash Flow</div>
            <h2 class="panel-title">Money in vs money out</h2>
            <p class="panel-sub">Daily customer pay-ins and pay-outs in EUR. Click a series to toggle, drag the date range to zoom.</p>
          </div>
        </div>

        <div class="chart-wrap">
          <div class="chart-meta">
            <div class="legend" id="chart-legend">
              <button class="legend-item" data-key="payIn">
                <span class="legend-mark" style="background: var(--accent);"></span>
                Money in
              </button>
              <button class="legend-item" data-key="payOut">
                <span class="legend-mark is-dashed" style="color: var(--ink-secondary);"></span>
                Money out
              </button>
            </div>
            <div class="chart-net" id="chart-net">In · Out · Net</div>
          </div>
          <div class="chart-host" id="chart-host"></div>
        </div>

        <div class="panel-body" style="padding: 1.25rem 1.5rem; border-top: 1px solid var(--line-default);">
          <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.85rem;">
            <div>
              <div class="panel-eyebrow" style="margin-bottom: 0.2rem;">Method mix</div>
              <h3 style="margin: 0; font-size: 0.92rem; font-weight: 700;">How customers paid this week</h3>
            </div>
            <a class="link-arrow" data-route="pay-ins">Open Pay-Ins ${icon("arrowRight", 13)}</a>
          </div>
          <div style="height: 10px; display: flex; gap: 2px; background: var(--surface-canvas); margin-bottom: 0.85rem;">
            ${methodMix
              .map(
                (m) =>
                  `<div style="width: ${m.pct}%; background: ${
                    m.key === "wallet"
                      ? "var(--accent)"
                      : m.key === "card"
                        ? "color-mix(in srgb, var(--accent) 55%, var(--ink-muted))"
                        : m.key === "crypto"
                          ? "var(--ink-primary)"
                          : "var(--ink-muted)"
                  };"></div>`,
              )
              .join("")}
          </div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.75rem;">
            ${methodMix
              .map(
                (m) => `<div style="padding-top: 0.45rem; border-top: 2px solid ${
                  m.key === "wallet"
                    ? "var(--accent)"
                    : m.key === "card"
                      ? "color-mix(in srgb, var(--accent) 55%, var(--ink-muted))"
                      : m.key === "crypto"
                        ? "var(--ink-primary)"
                        : "var(--ink-muted)"
                };">
                <span style="font-size: 0.6rem; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">${m.label}</span>
                <div style="font-weight: 200; font-size: 1.45rem; letter-spacing: -0.03em; line-height: 1; font-variant-numeric: tabular-nums;">${m.pct}<small style="font-size: 0.55em; color: var(--ink-muted);">%</small></div>
                <span style="font-size: 0.68rem; color: var(--ink-subtle); font-variant-numeric: tabular-nums;">${fmtEur(m.value, { compact: true })}</span>
              </div>`,
              )
              .join("")}
          </div>
        </div>
      </article>

      <div class="stack">
        <article class="actions-panel">
          <span class="actions-panel-watermark" aria-hidden="true"></span>
          <span class="actions-panel-eyebrow">Today's instructions</span>
          <h2 class="actions-panel-title">Three actions worth your <em>attention.</em></h2>
          <p class="actions-panel-sub">Recommended by the ledger, sequenced by impact.</p>
          <div class="actions-list">
            <button class="action-key" data-route="settlement">
              <span class="action-key-num"><strong>I.</strong></span>
              <span class="action-key-body">
                <strong>Run settlement</strong>
                <span>€553,172 ready · into Acc. EE38…417</span>
              </span>
              <span class="action-key-arrow">→</span>
            </button>
            <button class="action-key" data-route="pay-outs">
              <span class="action-key-num"><strong>II.</strong></span>
              <span class="action-key-body">
                <strong>Approve 7 pay-outs</strong>
                <span>€84,210 · awaiting your sign-off</span>
              </span>
              <span class="action-key-arrow">→</span>
            </button>
            <button class="action-key" data-route="liquidity">
              <span class="action-key-num"><strong>III.</strong></span>
              <span class="action-key-body">
                <strong>Top up payout liquidity</strong>
                <span>3 days runway at current rate</span>
              </span>
              <span class="action-key-arrow">→</span>
            </button>
          </div>
        </article>

        <article class="panel">
          <div class="panel-head">
            <div>
              <div class="panel-eyebrow">Digital Assets</div>
              <h2 class="panel-title">Crypto balances</h2>
            </div>
            <a class="link-arrow" data-route="crypto">All assets ${icon("arrowRight", 13)}</a>
          </div>
          <div class="panel-body" style="display: flex; flex-direction: column; gap: 1.2rem;">
            <div style="display: grid; grid-template-columns: 36px 1fr auto; align-items: center; gap: 0.85rem; padding-bottom: 0.85rem; border-bottom: 1px solid var(--line-subtle);">
              <span style="width: 36px; height: 36px; border-radius: 50%; background: var(--accent-soft); color: var(--accent); border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.66rem;">USDC</span>
              <div style="line-height: 1.2;">
                <strong style="display: block; font-weight: 600; font-size: 0.85rem;">USD Coin</strong>
                <span style="font-size: 0.68rem; color: var(--ink-muted); font-variant-numeric: tabular-nums; letter-spacing: 0.04em;">0xA1f4…29Bd · Solana</span>
              </div>
              <div style="text-align: right; line-height: 1.2;">
                <strong style="font-size: 1.05rem; font-weight: 200; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; display: block;">52,418.20</strong>
                <span style="font-size: 0.7rem; color: var(--ink-subtle); font-variant-numeric: tabular-nums;">≈ €48,940</span>
              </div>
            </div>
            <div style="display: grid; grid-template-columns: 36px 1fr auto; align-items: center; gap: 0.85rem;">
              <span style="width: 36px; height: 36px; border-radius: 50%; background: var(--surface-feature); color: var(--ink-on-feature); border: 1px solid var(--line-default); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.66rem;">EURC</span>
              <div style="line-height: 1.2;">
                <strong style="display: block; font-weight: 600; font-size: 0.85rem;">Euro Coin</strong>
                <span style="font-size: 0.68rem; color: var(--ink-muted); font-variant-numeric: tabular-nums; letter-spacing: 0.04em;">0x9c2…BA10 · Ethereum</span>
              </div>
              <div style="text-align: right; line-height: 1.2;">
                <strong style="font-size: 1.05rem; font-weight: 200; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; display: block;">27,350.00</strong>
                <span style="font-size: 0.7rem; color: var(--ink-subtle); font-variant-numeric: tabular-nums;">≈ €27,350</span>
              </div>
            </div>
            <div style="display: flex; gap: 0.5rem;">
              <button class="btn btn-primary btn-sm">Convert ${icon("swap", 11)}</button>
              <button class="btn btn-ghost btn-sm">Send</button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="panel" style="margin-bottom: 1.5rem;">
      <div class="panel-head">
        <div>
          <div class="panel-eyebrow">Live Tape</div>
          <h2 class="panel-title">Recent activity</h2>
          <p class="panel-sub">Pay-ins, pay-outs, and business payments — newest first.</p>
        </div>
        <button class="btn btn-ghost btn-sm">Export ${icon("download", 11)}</button>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 90px 1.6fr 1fr 1fr 110px 30px;">
          <span>Date / Time</span>
          <span>Customer / Reference</span>
          <span>Method</span>
          <span style="text-align: right;">Amount</span>
          <span>Status</span>
          <span></span>
        </div>
        ${recent.map((t) => transactionRow(t)).join("")}
        <div class="ledger-table-foot">
          <span>Showing ${recent.length} of 170 entries · 06 May, 07:00 → 09:14 CET</span>
          <a class="link-arrow" data-route="pay-ins">Open full live tape ${icon("arrowRight", 13)}</a>
        </div>
      </div>
    </section>
  `;

  // Wire local nav clicks (to bypass router re-querying selectors)
  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });

  // Build the chart
  const host = ctx.canvas.querySelector<HTMLElement>("#chart-host");
  const netEl = ctx.canvas.querySelector<HTMLElement>("#chart-net");
  let chart: BalanceChart | null = null;
  if (host && netEl) {
    chart = buildBalanceChart({
      host,
      data: [
        chartSeries.timestamps,
        chartSeries.payIn,
        chartSeries.payOut,
      ] as const as never,
      series: [
        {
          key: "payIn",
          label: "Money in",
          width: 2.2,
          fill: true,
          color: () =>
            getComputedStyle(document.documentElement)
              .getPropertyValue("--accent")
              .trim(),
        },
        {
          key: "payOut",
          label: "Money out",
          width: 1.4,
          dash: [3, 3],
          color: () =>
            getComputedStyle(document.documentElement)
              .getPropertyValue("--ink-secondary")
              .trim(),
        },
      ],
      netEl,
    });
  }

  // Range buttons
  ctx.canvas
    .querySelectorAll<HTMLButtonElement>("#page-range button")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        ctx.canvas
          .querySelectorAll("#page-range button.is-active")
          .forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        const days = Number(btn.dataset.days);
        if (Number.isFinite(days)) chart?.setRange(days);
      });
    });

  // Legend toggles
  ctx.canvas
    .querySelectorAll<HTMLButtonElement>("#chart-legend .legend-item")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        btn.classList.toggle("is-off");
        const key = btn.dataset.key as string;
        chart?.setVisible(key, !btn.classList.contains("is-off"));
      });
    });

  return () => {
    chart?.destroy();
  };
}
